<template>
  <div v-studio-click-outside="closeMultiselect" class="tw-flex">
    <div class="tw-relative">
      <slot name="trigger" />
      <transition name="slide">
        <div
          v-if="isOpen"
          id="multiselect-list"
          data-testid="studio-multiselect"
          class="tw-absolute tw-z-50 tw-mt-1 tw-w-72 tw-rounded-md tw-bg-white tw-py-1.5 tw-shadow-md"
          :class="extraClasses"
        >
          <div v-if="shouldShowSearchInput" class="tw-flex tw-w-full tw-p-2">
            <StudioSearchInput
              alwaysExpanded
              :searchTerm="searchTerm"
              @onSearch="searchTerm => $emit('onSearch', searchTerm)"
            />
          </div>
          <InfiniteScrollContainer
            class="tw-max-h-[300px] tw-space-y-1 tw-overflow-y-auto tw-pb-1 tw-text-sm"
            :pause="isLoadingData"
            @scroll-bottom="$emit('scrolledToBottom')"
          >
            <div v-if="isLoadingData && !fetchSilently" class="tw-flex tw-items-center tw-justify-center tw-py-4">
              <StudioSpinner size="small" :coverContainer="false" />
            </div>
            <slot v-else-if="multiselectItems.length === 0 && !isLoadingData" name="empty-state" />
            <template v-else>
              <div
                v-for="item in multiselectItems"
                :id="item.id"
                :key="item.id"
                :data-testid="item.id"
                class="tw-flex tw-items-center tw-space-x-2 tw-rounded tw-px-3 tw-py-1.5 hover:tw-bg-slate-100"
              >
                <StudioCheckbox
                  :id="`${item.id}-checkbox`"
                  :checked="item.selected"
                  :data-testid="`${item.id}-checkbox`"
                  @click="$emit('onToggleSelectItem', item.id)"
                />
                <label
                  :for="`${item.id}-checkbox`"
                  class="tw-cursor-pointer tw-break-all tw-text-left tw-font-normal tw-leading-tight"
                >
                  {{ item.title }}
                  <span v-if="item.subtitle" class="tw-text-xs tw-text-slate-500">
                    <br />
                    {{ item.subtitle }}
                  </span>
                </label>
              </div>
            </template>
            <slot name="footer" />
          </InfiniteScrollContainer>
        </div>
      </transition>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

import { StudioSearchInput, StudioSpinner } from '.';
import StudioCheckbox from './StudioCheckbox.vue';

import InfiniteScrollContainer from '@/studio/studioLibrary/InfiniteScrollContainer.vue';

export interface MultiselectItem {
  title: string;
  id: string;
  selected: boolean;
  subtitle?: string;
}

export default defineComponent({
  name: 'StudioMultiselect',
  components: {
    StudioSpinner,
    StudioSearchInput,
    StudioCheckbox,
    InfiniteScrollContainer
  },
  props: {
    multiselectItems: {
      type: Array as () => MultiselectItem[],
      required: true
    },
    isOpen: {
      type: Boolean,
      default: false
    },
    searchTerm: {
      type: String,
      default: ''
    },
    shouldShowSearchInput: {
      type: Boolean,
      default: false
    },
    isLoadingData: {
      type: Boolean,
      default: false
    },
    extraClasses: {
      type: String,
      default: ''
    },
    fetchSilently: { type: Boolean, default: false }
  },
  emits: {
    scrolledToBottom: () => true,
    close: () => true,
    onSearch: (_searchTerm: string) => true,
    onToggleSelectItem: (_selectedItemId: string) => true
  },
  methods: {
    closeMultiselect() {
      if (this.isOpen) {
        this.$emit('close');
      }
    }
  }
});
</script>

<style scoped>
.slide-enter-active,
.slide-leave-active {
  transition: all 0.15s ease;
}
.slide-enter-from,
.slide-leave-to {
  opacity: 0;
  transform: translateY(10%);
}
</style>
